import {inject, Injectable} from "@angular/core";
import {VatRateRepo} from "../repositories/api/vat-rate.repo";
import {map} from "rxjs/operators";
import {VatRateDto} from "../repositories/dto/vat-rate.dto";
import {AbstractService} from "./abstract.service";
import {BehaviorSubject, Observable, of} from "rxjs";
import {FormSelectOption} from "../libs/form/items/form-select-option";
import {VatRate} from "../libs/enum/vat-rate.enum";
import {KeyValueDto} from "../repositories/dto/key-value.dto";
import {VoidDto} from "../repositories/dto/void.dto";
import {VatRateState} from "../libs/enum/vat-rate-state";
import {RepoProvider} from "../repositories/repo.provider";
import {isDefined, isUuid} from "../libs/utils/epic.utils";
import {OrgDto} from "../repositories/dto/org.dto";

@Injectable({providedIn: 'root'})
export class VatRateService extends AbstractService {

  private readonly vatRateRepo: VatRateRepo = inject(RepoProvider).getRepo(VatRateRepo);

  private readonly currentVatRates: BehaviorSubject<VatRateDto[]> = new BehaviorSubject<VatRateDto[]>([]);
  private readonly allVatRates: BehaviorSubject<VatRateDto[]> = new BehaviorSubject<VatRateDto[]>([]);
  private readonly defaultVatRate: BehaviorSubject<VatRateDto | undefined> = new BehaviorSubject<VatRateDto | undefined>(undefined);

  constructor() {
    super();
    this.stateService.getOrgDistinct().subscribe((o) => this.reloadAll(o));
  }

  public getCurrent(): Observable<VatRateDto[]> {
    return this.currentVatRates.asObservable();
  }

  public getAll(): Observable<VatRateDto[]> {
    return this.allVatRates.asObservable();
  }

  // noinspection JSUnusedGlobalSymbols
  public getDefault(): Observable<VatRateDto | undefined> {
    return this.defaultVatRate.asObservable();
  }

  // noinspection JSUnusedGlobalSymbols
  public getByVatRate(vatRate: VatRate): VatRateDto | undefined {
    return this.currentVatRates.value.find((v) => v.vatRate === vatRate);
  }

  public getByDate(date: Date): Observable<VatRateDto[]> {
    return this.vatRateRepo.readByDate(this.getOrg(), date)
      .pipe(map((w) => w.getDatas()));
  }

  public getDefaultVatRate(): VatRateDto | undefined {
    return this.defaultVatRate.value;
  }


  public getTypes(): Observable<KeyValueDto[]> {
    return this.vatRateRepo.readType(this.getOrg())
      .pipe(map((w) => w.getDatas()));
  }

  public update(vatRate: VatRateDto): Observable<VatRateDto> {
    return this.vatRateRepo.update(this.getOrg(), vatRate)
      .pipe(map((w) => w.getFirstData()));
  }

  public delete(id: string): Observable<VoidDto> {
    return this.vatRateRepo.delete(this.getOrg(), id)
      .pipe(map((w) => w.getFirstData()));
  }

  public buildSelectOptions(data: Observable<VatRateDto[]>): Observable<FormSelectOption[]> {
    return (isDefined(data) ? data : of([])).pipe(
      map((vs) => vs.filter((v) => v.vatRate !== VatRate.OLD_PART_STANDARD)),
      map((vs) => vs.filter((v) => v.vatRate !== VatRate.OLD_PART_CREDIT)),
      map((vs) => vs.map(v => this.buildSelectOption(v))));
  }

  public buildSelectOption(vr: VatRateDto): FormSelectOption {
    const name: string = vr.value + '% ' + this.i18n.translate('vatrate.' + String(vr.vatRate).toLowerCase());
    return new FormSelectOption(name, vr.vatRate).useName();
  }

  public reloadAll(org?: OrgDto): void {
    const o: OrgDto = isDefined(org) && isUuid(org.id) ? org : this.getOrg();
    if (isUuid(o.id)) {
      this.defaultVatRate.next(o.defaultVatRate);
      this.vatRateRepo.readAll(o)
        .pipe(map((w) => w.getDatas()))
        .subscribe((all) => {
          this.allVatRates.next(all);
          this.currentVatRates.next(all.filter((vr) => vr.state === VatRateState.CURRENT));
        });
    } else {
      this.defaultVatRate.next(undefined);
      this.allVatRates.next([]);
      this.currentVatRates.next([]);
    }
  }
}
